<template>
    <form class="form-group" novalidate @submit.prevent="onSubmit">
        <div id="loader" v-if="isLoading">
            <div class="spinner"></div>
        </div>
        <section class="section">
            <h1>Sign-up for My King’s Trust</h1>

            <p>
                Start your journey with The King's Trust. When you have completed
                this form, we'll send you an email to confirm your details. If
                you can't find it, please check in your junk folder. We need you
                to complete this form and click the link in the confirmation
                email so we can create your My King's Trust account.
            </p>

            <div class="form-row">
                <div class="form-group col-lg-6">
                    <label class="form-label" for="first_name"
                        ><b>First name</b></label
                    >
                    <input
                        id="first_name"
                        v-model.trim="form.first_name"
                        class="form-control"
                        name="first_name"
                        type="text"
                        autocomplete="given-name"
                    />
                    <span
                        v-if="errors.first_name"
                        class="enquiry-errors"
                        role="alert"
                        aria-live="assertive"
                        >{{ errors.first_name }}</span
                    >
                </div>

                <div class="form-group col-lg-6">
                    <label class="form-label" for="last_name"
                        ><b>Last name</b></label
                    >
                    <input
                        id="last_name"
                        v-model.trim="form.last_name"
                        class="form-control"
                        name="last_name"
                        type="text"
                        autocomplete="family-name"
                    />
                    <span
                        v-if="errors.last_name"
                        class="enquiry-errors"
                        role="alert"
                        aria-live="assertive"
                        >{{ errors.last_name }}</span
                    >
                </div>
            </div>

            <div class="form-row">
                <div class="form-group col-lg-6">
                    <label class="form-label" for="date_of_birth"
                        ><b>Date of birth (DD/MM/YYYY)</b></label
                    >
                    <div>
                        <div class="date-input-field">
                            <div>
                                <label
                                    class="input-label form-label"
                                    for="dob-day"
                                    >Day</label
                                >
                                <input
                                    id="dob-day"
                                    v-model="form.day"
                                    class="form-control input-width"
                                    name="dob-day"
                                    type="text"
                                    inputmode="numeric"
                                    maxlength="2"
                                    @input="moveToNextField($event)"
                                />
                            </div>
                        </div>
                        <div class="date-input-field">
                            <div>
                                <label
                                    class="input-label form-label"
                                    for="dob-month"
                                    >Month</label
                                >
                                <input
                                    id="dob-month"
                                    v-model="form.month"
                                    class="form-control input-width"
                                    name="dob-month"
                                    type="text"
                                    inputmode="numeric"
                                    maxlength="2"
                                    @input="moveToNextField($event)"
                                />
                            </div>
                        </div>
                        <div class="date-input-field">
                            <div>
                                <label
                                    class="input-label form-label"
                                    for="dob-year"
                                    >Year</label
                                >
                                <input
                                    id="dob-year"
                                    v-model="form.year"
                                    class="form-control input-width-2"
                                    name="dob-year"
                                    type="text"
                                    inputmode="numeric"
                                    maxlength="4"
                                />
                            </div>
                        </div>
                    </div>
                    <span
                        v-if="errors.date_of_birth"
                        class="enquiry-errors"
                        role="alert"
                        aria-live="assertive"
                        >{{ errors.date_of_birth }}</span
                    >
                </div>

                <div class="form-group col-lg-6">
                    <label class="form-label" for="email_address"
                        ><b>Email</b></label
                    >
                    <input
                        id="email_address"
                        v-model.trim="form.email_address"
                        class="form-control"
                        name="email_address"
                        type="email"
                        autocomplete="email"
                    />
                    <span
                        v-if="errors.email_address"
                        class="enquiry-errors"
                        role="alert"
                        aria-live="assertive"
                        >{{ errors.email_address }}</span
                    >
                </div>
            </div>

            <div class="form-group py-3 d-flex align-items-center gap-3">
                <button class="btn btn-primary" type="submit" :disabled="isLoading">Continue</button>
                <a href="/help" class="btn btn-link p-0">Need help?</a>
            </div>
        </section>
    </form>
</template>

<script setup>
import Form from "../Form.js";
import { reactive, markRaw, ref } from "vue";

const form = reactive({
    first_name: "",
    last_name: "",
    day: "",
    month: "",
    year: "",
    email_address: "",
});

const isLoading = ref(false)

let formRef = markRaw(ref(new Form({
    first_name: "",
    last_name: "",
    date_of_birth: "",
    email_address: "",
})))

const errors = reactive({
    first_name: "",
    last_name: "",
    date_of_birth: "",
    email_address: "",
});

function resetErrors() {
    errors.first_name = "";
    errors.last_name = "";
    errors.date_of_birth = "";
    errors.email_address = "";
}

function moveToNextField(event) {
    const currentInput = event.target;
    currentInput.value = currentInput.value.replace(/\D/g, "");

    const maxLength = Number(currentInput.getAttribute("maxlength"));
    if (currentInput.value.length === maxLength) {
        const formElements = Array.from(currentInput.form.elements);
        const currentIndex = formElements.indexOf(currentInput);
        const nextInput = formElements[currentIndex + 1];

        if (nextInput && nextInput.tagName === "INPUT") {
            nextInput.focus();
        }
    }
}

function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function getDobString() {
    return `${form.day}/${form.month}/${form.year}`;
}

function parseDob(dob) {
    const [day, month, year] = dob.split("/").map(Number);
    const parsedDate = new Date(year, month - 1, day);

    const isRealDate =
        parsedDate.getFullYear() === year &&
        parsedDate.getMonth() === month - 1 &&
        parsedDate.getDate() === day;

    return isRealDate ? parsedDate : null;
}

function isAgeWithinRange(dobDate, minimumAge, maximumAge) {
    const today = new Date();
    let age = today.getFullYear() - dobDate.getFullYear();
    const monthDifference = today.getMonth() - dobDate.getMonth();

    if (
        monthDifference < 0 ||
        (monthDifference === 0 && today.getDate() < dobDate.getDate())
    ) {
        age--;
    }

    return age >= minimumAge && age <= maximumAge;
}

async function onSubmit() {
    resetErrors();

    if (!form.first_name) {
        errors.first_name = "We need your first name to complete this form.";
    }

    if (!form.last_name) {
        errors.last_name = "We need your last name to complete this form.";
    }

    if (!form.day || !form.month || !form.year) {
        errors.date_of_birth = "We need your date of birth to complete this form.";
    } else {
        const dob = getDobString();

        if (!/^\d{2}\/\d{2}\/\d{4}$/.test(dob)) {
            errors.date_of_birth =
                "Date of birth must be in DD/MM/YYYY format.";
        }

        const dobDate = parseDob(dob);

        if (!errors.date_of_birth && !dobDate) {
            errors.date_of_birth = "Enter a valid date of birth.";
        } else if (
            !errors.date_of_birth &&
            !isAgeWithinRange(dobDate, 16, 30)
        ) {
            errors.date_of_birth = "Our courses are for young people aged between 16 and 30.";
        }
    }

    if (!form.email_address) {
        errors.email_address = "We need your email address to complete this form.";
    } else if (!isValidEmail(form.email_address)) {
        errors.email_address = "Enter a valid email address.";
    }

    const hasErrors = Object.values(errors).some((error) => Boolean(error));

    // submit form data if there are no errors - replace with actual submission logic
    if (!hasErrors) {

        formRef.value.first_name = form.first_name;
        formRef.value.last_name = form.last_name;
        formRef.value.email_address = form.email_address;
        formRef.value.date_of_birth = getDobString();

        isLoading.value = true;
        try {
            const response = await formRef.value.post("/create-account");
            window.location.href = response.redirect;
        } catch (error) {
            window.location.href = "/create-account/error";
        } finally {
            isLoading.value = false;
        }
    }
}
</script>
